import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useLocation } from "react-router-dom";
import { RootState } from "../../app/store";
import { fetchMeal, rateMeal, saveUserMealForUser, uploadMealImage, calculateMealWeights, saveConsumedMeal } from "../../state/meals/mealSlice";
import { Meal as MealModel } from "../../models/Meal";

import {
  Box,
  Button,
  Typography,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { StarRating } from "../common/StarRating";
import { useTranslation } from "react-i18next";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ShareIcon from "@mui/icons-material/Share";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import OpacityIcon from "@mui/icons-material/Opacity";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { buildConsumedMealFoodstuffs } from '../../common/consumedMealPayload';
import { useNotifications } from "../common/notification/NotificationContext"
import { calculateNutritionFromMeal, isLocalMealPlaceholder, resolveFoodstuffWeightInGrams, resolveMealImageUrl } from "../../common/mealMetrics";
import { uiTokens } from "../../common/uiTokens";

export function Meal() {
  const dispatch = useDispatch<any>();
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [file, setFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>();
  const [cookingMode, setCookingMode] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoggingMeal, setIsLoggingMeal] = useState(false);

  const { mealId } = useParams();
  const { state } = useLocation();
  const adjustedMeal = (state as { adjustedMeal?: MealModel } | undefined)?.adjustedMeal;
  const isAdjustedSnapshot = adjustedMeal?.id === mealId;
  const mealState = useSelector((state: RootState) => state.meal)
  const userId = useSelector((state: RootState) => state.user.user.id)
  const displayMeal = (isAdjustedSnapshot ? adjustedMeal : mealState.meal) || {
    languageCode: MealModel.languageCode.CS,
    foodstuffs: [],
    recipeSteps: [],
  };

  useEffect(() => {
    if (mealId && !isAdjustedSnapshot) {
      dispatch(fetchMeal(mealId));
    }
  }, [dispatch, isAdjustedSnapshot, mealId]);

  useEffect(() => {
    setCurrentStep(0);
  }, [mealId, cookingMode]);

  useEffect(() => {
    setImageUrl(resolveMealImageUrl(displayMeal));
  }, [displayMeal]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (file && mealId) {
      // You can set aiGenerated = true or false based on your UI or form
      dispatch(uploadMealImage({ id: mealId, aiGenerated: false, file }));
    }
  };

  const nutrition = useMemo(() => calculateNutritionFromMeal(displayMeal), [displayMeal]);

  function mealRatingChangeHandler(rating: number) {
    dispatch(rateMeal({ mealId: displayMeal.id, newRating: rating }))
  }

  const handleAddToPlan = () => {
    if (!mealId || !userId) {
      return
    }

    dispatch(saveUserMealForUser({ userId, mealId }))
  }

  const handleCalculateWeights = () => {
    if (!mealId) {
      return
    }

    dispatch(calculateMealWeights(mealId))
  }

  const handleLogMeal = () => {
    if (!mealId || !displayMeal.id) {
      return
    }

    setIsLoggingMeal(true)
    dispatch(
      saveConsumedMeal({
        meal: {
          id: displayMeal.id,
          languageCode: displayMeal.languageCode,
        },
        name: displayMeal.name,
        foodstuffs: buildConsumedMealFoodstuffs(displayMeal.foodstuffs),
        mealtime: displayMeal.mealtime || guessedMealtime(),
        consumptionTime: new Date().toISOString().slice(0, 16),
      })
    )
      .unwrap()
      .then(() => {
        addNotification(t('recommended_meal.logged_toast'), "info")
      })
      .catch((error: any) => {
        addNotification(error?.message || t('recommended_meal.logged_failed'), "warning")
      })
      .finally(() => {
        setIsLoggingMeal(false)
      })
  }

  const ratingCount = displayMeal.mealRatingStats?.ratingCount || 0;
  const formatIngredientQuantity = (weight?: number, value?: number, symbol?: string) => {
    if (typeof weight === "number" && weight > 0) {
      const formatted = Number.isInteger(weight) ? weight.toFixed(0) : weight.toFixed(1);
      return `${formatted} g`;
    }

    if (typeof value === "number" && value > 0) {
      const formatted = Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1);
      return `${formatted}${symbol || ""}`;
    }

    return null;
  };

  const ingredients = (displayMeal.foodstuffs || []).filter((fs) => {
    const selectedUnit = fs.measurementUnits?.find((unit) => unit.selected);
    return Boolean(formatIngredientQuantity(resolveFoodstuffWeightInGrams(fs), fs.value, selectedUnit?.symbol));
  });
  const steps = displayMeal.recipeSteps || [];
  const showEditControls = Boolean(userId && displayMeal.author?.id === userId);
  const accentColor = "#7A4A2E";

  const nutritionChips = useMemo(() => ([
    { icon: <WhatshotIcon />, label: `${nutrition.energyKcal.toFixed(0)} kcal` },
    { icon: <FitnessCenterIcon />, label: `${nutrition.proteinG.toFixed(1)} g ${t('common.nutrient.PROTEIN')}` },
    { icon: <RestaurantIcon />, label: `${nutrition.carbohydrateG.toFixed(1)} g ${t('common.nutrient.CARBOHYDRATE')}` },
    { icon: <OpacityIcon />, label: `${nutrition.fatG.toFixed(1)} g ${t('common.nutrient.FAT')}` }
  ]), [nutrition, t]);

const guessedMealtime = () => {
    const hour = new Date().getHours();
    if (hour < 10) return 'BREAKFAST';
    if (hour < 12) return 'SNACK_AM';
    if (hour < 15) return 'LUNCH';
    if (hour < 18) return 'SNACK_PM';
    return 'DINNER';
  };

  const renderStepText = (step: string) => {
    const trimmed = step.trim();
    if (!trimmed) return step;
    const [first, ...rest] = trimmed.split(" ");
    return (
      <Typography variant="body1" sx={{ color: "#2E2A26" }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {first}
        </Box>
        {rest.length > 0 ? ` ${rest.join(" ")}` : ""}
      </Typography>
    );
  };


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        maxWidth: 900,
        mx: "auto",
        pt: { xs: 2, md: 4 },
        pb: { xs: 10, md: 4 },
        px: { xs: 2, md: 3 },
        gap: { xs: 2.5, md: 3.5 }
      }}
    >
      {/* Header Section */}
      <Box sx={{ width: "100%", textAlign: "center" }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          component="h1"
          sx={{ fontWeight: 700, color: "#2E2A26", mb: 1 }}
        >
          {displayMeal.name}
        </Typography>
        {isAdjustedSnapshot && (
          <>
            <Chip
              label={t('meal.adjusted_version_badge')}
              sx={{ mb: 1.5, backgroundColor: uiTokens.colors.adaptiveBg, color: uiTokens.colors.adaptiveText, fontWeight: 700 }}
            />
            <Typography variant="body2" sx={{ color: "#6F675F", mb: 1.5 }}>
              {t('meal.adjusted_recipe_note')}
            </Typography>
          </>
        )}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <StarRating
            averageRating={displayMeal.mealRatingStats?.averageRating || 0}
            ratingCount={ratingCount}
            userRating={displayMeal.currentUserRating}
            onRatingChange={(rating) => mealRatingChangeHandler(rating)}
          />
        </Box>
      </Box>
      {/* Hero Image Section */}

      {displayMeal.images?.[0] && (() => {
        const { aiGenerated } = displayMeal.images[0];
        const resolvedImageUrl = imageUrl || resolveMealImageUrl(displayMeal);
        const isPlaceholderImage = isLocalMealPlaceholder(resolvedImageUrl);
        return (
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: 760 },
              backgroundColor: "#F7F2EC",
              borderRadius: 3,
              p: { xs: 1.5, md: 2 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <img
              src={resolvedImageUrl}
              alt={displayMeal.name || "Meal"}
              onError={() => setImageUrl('/mealPlaceholder.png')}
              style={{
                width: "100%",
                borderRadius: 12,
                display: "block",
                objectFit: isPlaceholderImage ? "contain" : "cover",
                backgroundColor: isPlaceholderImage ? "#F3EEE8" : "transparent",
                padding: isPlaceholderImage ? "16px" : "0",
                minHeight: isPlaceholderImage ? "280px" : undefined,
              }}
            />

            {aiGenerated && (
              <Box
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  backgroundColor: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  padding: "4px 10px",
                  borderRadius: 999,
                  whiteSpace: "nowrap",
                  zIndex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    display: "inline-block",
                    textAlign: "center",
                  }}
                >
                  {t('meal.ai_generated')}
                </Typography>
              </Box>
            )}

            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
              {nutritionChips.map((chip, index) => (
                <Chip
                  key={index}
                  icon={chip.icon}
                  label={chip.label}
                  sx={{
                    backgroundColor: "#FFF8EE",
                    color: "#4C4743",
                    borderRadius: 999,
                    fontWeight: 600,
                    "& .MuiChip-icon": { color: accentColor },
                  }}
                />
              ))}
            </Box>
          </Box>
        );
      })()}
      {!displayMeal.images?.[0] && (
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", md: 760 },
            backgroundColor: "#F7F2EC",
            borderRadius: 3,
            p: { xs: 2, md: 3 },
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {nutritionChips.map((chip, index) => (
            <Chip
              key={index}
              icon={chip.icon}
              label={chip.label}
              sx={{
                backgroundColor: "#FFF8EE",
                color: "#4C4743",
                borderRadius: 999,
                fontWeight: 600,
                "& .MuiChip-icon": { color: accentColor },
              }}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          width: "100%",
          mt: 1.5,
        }}
      >
        <Button
          variant="outlined"
          size={isMobile ? "medium" : "small"}
          startIcon={<RestaurantIcon />}
          sx={{ borderColor: "#D7CFC6", color: "#6F675F", flex: 1 }}
          onClick={handleLogMeal}
          disabled={isLoggingMeal}
        >
          Zaznamenat
        </Button>

        <Button
          variant="outlined"
          size={isMobile ? "medium" : "small"}
          sx={{ borderColor: "#D7CFC6", color: "#6F675F", flex: 1 }}
          onClick={handleCalculateWeights}
        >
          Přepočítat váhy
        </Button>
        <Button
          variant="outlined"
          size={isMobile ? "medium" : "small"}
          startIcon={<StarBorderIcon />}
          sx={{ borderColor: "#D7CFC6", color: "#6F675F", flex: 1 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          {t('meal.action_rate')}
        </Button>
        <Button
          variant="outlined"
          size={isMobile ? "medium" : "small"}
          startIcon={<ShareIcon />}
          sx={{ borderColor: "#D7CFC6", color: "#6F675F", flex: 1 }}
        >
          {t('meal.action_share')}
        </Button>
      </Box>
      {/* Ingredients Section */}
      <Box sx={{ width: "100%", backgroundColor: "#F9F5F0", borderRadius: 3, p: { xs: 2, md: 3 } }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: "#2E2A26" }}>
          {t('meal.ingredients')}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {ingredients.map((fs, index) => {
            const key = `${fs.id || fs.name || index}`;
            const selectedUnit = fs.measurementUnits?.find((unit) => unit.selected);
            const quantity = formatIngredientQuantity(resolveFoodstuffWeightInGrams(fs), fs.value, selectedUnit?.symbol);

            if (!quantity) {
              return null;
            }

            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  borderRadius: 2,
                  padding: "8px 12px",
                  backgroundColor: "#FFFDF9",
                }}
              >
                <Typography sx={{ color: "#2E2A26", fontWeight: 600 }}>
                  {fs.name}
                </Typography>
                <Typography sx={{ color: "#6F675F", fontWeight: 500 }}>
                  {quantity}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Recipe Steps Section */}
      <Box sx={{ width: "100%", backgroundColor: "#F9F5F0", borderRadius: 3, p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#2E2A26" }}>
            {t('meal.how_to_cook')}
          </Typography>
          {isMobile && steps.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setCookingMode((prev) => !prev)}
              sx={{ borderColor: "#D7CFC6", color: "#6F675F", textTransform: "none" }}
            >
              {cookingMode ? t('meal.cooking_mode_off') : t('meal.cooking_mode_on')}
            </Button>
          )}
        </Box>

        {cookingMode && steps.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                backgroundColor: "#FFFDF9",
                borderRadius: 2,
                p: 2,
                minHeight: 140,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: accentColor,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                  }}
                >
                  {currentStep + 1}
                </Box>
                <Box sx={{ fontSize: "1.1rem" }}>
                  {renderStepText(steps[currentStep] || "")}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
                disabled={currentStep === 0}
                sx={{ borderColor: "#D7CFC6", color: "#6F675F", flex: 1 }}
              >
                {t('meal.prev_step')}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1))}
                disabled={currentStep >= steps.length - 1}
                sx={{ backgroundColor: accentColor, flex: 1, "&:hover": { backgroundColor: "#5F3923" } }}
              >
                {t('meal.next_step')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {steps.map((step, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  gap: 2,
                  backgroundColor: "#FFFDF9",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Box
                  sx={{
                    minWidth: 30,
                    height: 30,
                    borderRadius: "50%",
                    backgroundColor: "#E6DED6",
                    color: "#4C4743",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                  }}
                >
                  {i + 1}
                </Box>
                <Box sx={{ flex: 1 }}>{renderStepText(step)}</Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Nutritional Information Section */}
      <Box sx={{ width: "100%" }}>
        <Accordion
          sx={{
            backgroundColor: "#F9F5F0",
            borderRadius: 3,
            boxShadow: "none",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700, color: "#2E2A26" }}>
              {t('meal.nutrition_per_serving')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {[
                { label: t('common.nutrient.ENERGY'), value: `${nutrition.energyKcal.toFixed(1)} kcal` },
                { label: t('common.nutrient.PROTEIN'), value: `${nutrition.proteinG.toFixed(1)} g` },
                { label: t('common.nutrient.FAT'), value: `${nutrition.fatG.toFixed(1)} g` },
                { label: t('common.nutrient.CARBOHYDRATE'), value: `${nutrition.carbohydrateG.toFixed(1)} g` },
                { label: t('meal.fibre'), value: `${nutrition.fibreG.toFixed(1)} g` },
              ].map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: "#FFFDF9",
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Typography sx={{ color: "#4C4743", fontWeight: 600 }}>{row.label}</Typography>
                  <Typography sx={{ color: "#2E2A26", fontWeight: 700 }}>{row.value}</Typography>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Utility panel / footer with file upload controls */}
      {showEditControls && (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#F9F5F0",
            borderRadius: 3,
            p: { xs: 2, md: 3 }
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#2E2A26" }}>
            {t('meal.edit_recipe')}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <input
            type="file"
            id="upload-file"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <label htmlFor="upload-file">
            <Button variant="outlined" size="small" component="span">
              {t('meal.choose_file')}
            </Button>
          </label>
          <Button
            variant="contained"
            size="small"
            onClick={handleUpload}
            sx={{ ml: 2 }}
          >
            {t('meal.upload')}
          </Button>
        </Box>)}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#FFFDF9",
            borderTop: "1px solid #E6DED6",
            px: 2,
            py: 1,
            display: "flex",
            gap: 1,
            zIndex: 1200,
          }}
        >
              <Button
                variant="outlined"
                size="large"
                sx={{ borderColor: "#D7CFC6", color: "#6F675F", flex: 1 }}
                onClick={handleCalculateWeights}
              >
                Přepočítat váhy
              </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<RestaurantIcon />}
            sx={{ borderColor: "#D7CFC6", color: "#6F675F", flex: 1 }}
            onClick={handleLogMeal}
            disabled={isLoggingMeal}
          >
            Zaznamenat
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<StarBorderIcon />}
            sx={{ borderColor: "#D7CFC6", color: "#6F675F", flex: 1 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            {t('meal.action_rate')}
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleAddToPlan}
            sx={{ backgroundColor: accentColor, flex: 1, "&:hover": { backgroundColor: "#5F3923" } }}
          >
            {t('meal.action_add_to_plan')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
